/**
 * A live session whose turn ended with work of its own still out (ORGU-78: a background command,
 * agent or monitor, or a ScheduleWakeup not yet due) waits on itself, not on its person. The agent
 * names that work under `Session.ext.ownWork` so the drawer, rows and missions say what it waits
 * on ("Waiting on its tests: pnpm test:affected, running 5 min") instead of Working (ORGU-132).
 * `Session.ext.currentStart` is when the current run began: the latest launch or wake, so a
 * session woken several times is never read as one long run. Browser-safe.
 */
import type { Session, Ts } from '../index.ts';

/** The key under Session.ext: the session's own work outstanding after its turn ended (OwnWork). */
export const SESSION_OWN_WORK_EXT = 'ownWork';
/** The key under Session.ext: when its current run began (the latest launch or wake), a Ts. */
export const SESSION_CURRENT_START_EXT = 'currentStart';

export interface OwnWorkItem {
  kind: 'command' | 'agent' | 'monitor' | 'wakeup';
  /** The command, or the monitor's or agent's description; null when the transcript did not say. */
  what: string | null;
  /** When it started. */
  at: Ts | null;
  /** A wakeup: when it is due. */
  due: Ts | null;
}

export interface OwnWork {
  /** When the turn that left this work out ended. */
  turnEndedAt: Ts | null;
  /** Oldest first. */
  work: OwnWorkItem[];
}

/** At most this many items travel. */
export const OWN_WORK_MAX = 5;

/** The session's own outstanding work, or null (none, or an older agent). */
export function ownWorkOf(s: Partial<Pick<Session, 'ext' | 'status'>> | null | undefined): OwnWork | null {
  if (!s || s.status === 'ended') return null;
  const v = s.ext?.[SESSION_OWN_WORK_EXT] as OwnWork | undefined;
  return v && Array.isArray(v.work) && v.work.length > 0 ? v : null;
}

/** When the session's current run began: the latest launch or wake, else its first start. */
export function currentStartOf(s: Pick<Session, 'startedAt'> & Partial<Pick<Session, 'ext'>>): Ts {
  const v = s.ext?.[SESSION_CURRENT_START_EXT];
  return typeof v === 'number' && Number.isFinite(v) && v >= s.startedAt ? v : s.startedAt;
}

const TEST_RE = /\b(?:pnpm|npm|yarn|bun)\s+(?:run\s+)?test(?::[\w-]+)?(?![\w-])|\b(?:vitest|jest|pytest)\b|\b(?:go|cargo)\s+test\b/;

/** "5 min", "1 h 05 min", "under a minute". */
export function shortSpan(ms: number): string {
  const min = Math.floor(Math.max(0, ms) / 60_000);
  if (min < 1) return 'under a minute';
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')} min`;
}

/** What one item is, as the owner reads it: its tests, its command, its agent, its monitor. */
export function ownWorkNoun(w: Pick<OwnWorkItem, 'kind' | 'what'>): string {
  if (w.kind !== 'agent' && w.what && TEST_RE.test(w.what)) return 'its tests';
  if (w.kind === 'agent') return 'its agent';
  if (w.kind === 'monitor') return 'its monitor';
  if (w.kind === 'wakeup') return 'its wake-up';
  return 'its command';
}

/**
 * One line for what a session waits on: "Waiting on its tests: pnpm test:affected, running 5 min",
 * "Waiting on its agent: Review the diff, running 2 min, and 1 more", "Waking in 12 min".
 */
export function ownWorkLine(o: OwnWork, now: number): string {
  const w = o.work[0]!;
  const more = o.work.length > 1 ? `, and ${o.work.length - 1} more` : '';
  if (w.kind === 'wakeup') return `Waking in ${w.due !== null ? shortSpan(w.due - now + 59_999) : 'a while'}${more}`;
  const what = w.what ? `: ${w.what}` : '';
  const running = w.at !== null ? `, running ${shortSpan(now - w.at)}` : '';
  return `Waiting on ${ownWorkNoun(w)}${what}${running}${more}`;
}
